// useAuth Hook - Core Authentication Hook
import { useSelector, useDispatch } from 'react-redux';
import { useCallback, useMemo } from 'react';
import {
  login,
  logout,
  updateUser,
  updateUserProfile,
  loadUserPermissions,
  clearPermissions,
  setCurrentOrganization,
} from '../../../Features/Authentication/authSlice';

/**
 * Core authentication hook providing user state and auth actions
 *
 * @returns {Object} Authentication state and methods
 */
export const useAuth = () => {
  const dispatch = useDispatch();

  // Select auth state from Redux store
  const {
    user,
    token,
    refreshToken,
    organizations,
    currentOrganization,
    roles,
    permissions,
    maxRoleLevel,
    status,
    updateStatus,
    permissionsStatus,
    orgSwitchStatus,
    error,
  } = useSelector((state) => state.auth);

  // Memoized authentication status
  const isAuthenticated = useMemo(() => !!token && !!user, [token, user]);
  const isLoading = useMemo(() => status === 'loading', [status]);
  const isUpdating = useMemo(() => updateStatus === 'loading', [updateStatus]);
  const isLoadingPermissions = useMemo(() => permissionsStatus === 'loading', [permissionsStatus]);
  const isSwitchingOrg = useMemo(() => orgSwitchStatus === 'loading', [orgSwitchStatus]);
  const hasError = useMemo(() => !!error, [error]);

  // RBAC-related memoized values
  const hasMultipleOrganizations = useMemo(() => organizations.length > 1, [organizations]);
  const currentOrgName = useMemo(() => currentOrganization?.name || '', [currentOrganization]);
  const currentOrgType = useMemo(() => currentOrganization?.type || '', [currentOrganization]);
  const isSuperAdmin = useMemo(() => maxRoleLevel >= 3, [maxRoleLevel]);
  const isAdmin = useMemo(() => maxRoleLevel >= 2, [maxRoleLevel]);
  const isSupervisor = useMemo(() => maxRoleLevel >= 1, [maxRoleLevel]);

  // Authentication actions
  const signIn = useCallback(
    async (credentials) => {
      try {
        const result = await dispatch(login(credentials)).unwrap();
        return { success: true, data: result };
      } catch (error) {
        return { success: false, error };
      }
    },
    [dispatch]
  );

  const signOut = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  const updateProfile = useCallback(
    async (profileData) => {
      try {
        const result = await dispatch(updateUser(profileData)).unwrap();
        return { success: true, data: result };
      } catch (error) {
        return { success: false, error };
      }
    },
    [dispatch]
  );

  const updateProfileSync = useCallback(
    (profileData) => {
      dispatch(updateUserProfile(profileData));
    },
    [dispatch]
  );

  // RBAC actions
  //
  // Switching organisation is deliberately a PER-SESSION, client-side change. The
  // server-side `switchOrganization` thunk flips `is_primary` in the database,
  // which is shared state: two open tabs (or a phone) would fight over which
  // organisation is current, and the change would outlive the session. Selecting
  // locally and sending the choice as a request header keeps each session
  // independent — see the X-Organization-Id header in baseQueryWithReauth.
  const switchCurrentOrganization = useCallback(
    async (organizationId) => {
      const target = organizations.find((org) => String(org.id) === String(organizationId));
      if (!target) {
        return { success: false, error: 'You do not have access to that organization' };
      }

      dispatch(setCurrentOrganization(target));
      // Drop cached permissions so they are reloaded for the new organisation.
      dispatch(clearPermissions());
      return { success: true, data: target };
    },
    [dispatch, organizations]
  );

  const refreshPermissions = useCallback(async () => {
    try {
      const result = await dispatch(loadUserPermissions()).unwrap();
      return { success: true, data: result };
    } catch (error) {
      return { success: false, error };
    }
  }, [dispatch]);

  const setOrganization = useCallback(
    (organization) => {
      dispatch(setCurrentOrganization(organization));
    },
    [dispatch]
  );

  // User information getters
  const getUserInfo = useCallback(
    (field) => {
      if (!user) return null;
      return field ? user[field] : user;
    },
    [user]
  );

  const getUserFullName = useCallback(() => {
    if (!user) return '';
    return `${user.first_name || ''} ${user.last_name || ''}`.trim();
  }, [user]);

  const getUserInitials = useCallback(() => {
    if (!user) return '';
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }, [user]);

  // Check if user has specific properties
  const hasUserProperty = useCallback(
    (property) => {
      return (
        user &&
        user.hasOwnProperty(property) &&
        user[property] !== null &&
        user[property] !== undefined
      );
    },
    [user]
  );

  // RBAC permission checking
  const hasPermission = useCallback(
    (permission) => {
      if (!permissions || permissions.length === 0) return false;
      return permissions.includes(permission);
    },
    [permissions]
  );

  const hasAnyPermission = useCallback(
    (permissionsList) => {
      if (!permissions || permissions.length === 0) return false;
      return permissionsList.some((permission) => permissions.includes(permission));
    },
    [permissions]
  );

  const hasAllPermissions = useCallback(
    (permissionsList) => {
      if (!permissions || permissions.length === 0) return false;
      return permissionsList.every((permission) => permissions.includes(permission));
    },
    [permissions]
  );

  const hasRole = useCallback(
    (roleName) => {
      if (!roles || roles.length === 0) return false;
      return roles.some((role) => role.name === roleName);
    },
    [roles]
  );

  const hasMinimumRoleLevel = useCallback(
    (level) => {
      return maxRoleLevel >= level;
    },
    [maxRoleLevel]
  );

  const canAccessOrganization = useCallback(
    (organizationId) => {
      if (!organizations || organizations.length === 0) return false;
      return organizations.some((org) => org.id === organizationId);
    },
    [organizations]
  );

  const getRoleNames = useCallback(() => {
    if (!roles || roles.length === 0) return [];
    return roles.map((role) => role.name);
  }, [roles]);

  const getHighestRole = useCallback(() => {
    if (!roles || roles.length === 0) return null;
    return roles.reduce((highest, current) => (current.level > highest.level ? current : highest));
  }, [roles]);

  // Session management
  const getToken = useCallback(() => token, [token]);

  const isTokenValid = useCallback(() => {
    if (!token) return false;

    try {
      // Basic token validation - check if it's not expired
      // This is a simple check, you might want to add JWT validation
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) return true; // Not a JWT, assume valid

      const payload = JSON.parse(atob(tokenParts[1]));
      const currentTime = Date.now() / 1000;

      return payload.exp > currentTime;
    } catch (error) {
      // If parsing fails, assume token is valid (might be a simple token)
      return true;
    }
  }, [token]);

  return {
    // State
    user,
    token,
    refreshToken,
    isAuthenticated,
    isLoading,
    isUpdating,
    isLoadingPermissions,
    isSwitchingOrg,
    hasError,
    error,
    status,
    updateStatus,
    permissionsStatus,
    orgSwitchStatus,

    // RBAC State
    organizations,
    currentOrganization,
    roles,
    permissions,
    maxRoleLevel,
    hasMultipleOrganizations,
    currentOrgName,
    currentOrgType,
    isSuperAdmin,
    isAdmin,
    isSupervisor,

    // Actions
    signIn,
    signOut,
    updateProfile,
    updateProfileSync,
    switchCurrentOrganization,
    refreshPermissions,
    setOrganization,

    // Permission Checking
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasMinimumRoleLevel,
    canAccessOrganization,
    getRoleNames,
    getHighestRole,

    // Getters
    getUserInfo,
    getUserFullName,
    getUserInitials,
    hasUserProperty,
    getToken,
    isTokenValid,
  };
};

export default useAuth;
